import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ListGroup, Button, Form, Spinner, Alert, Badge, Row, Col, ButtonGroup } from 'react-bootstrap';
import fileService from '../../services/fileService';
import './ProjectFiles.css'; // Import the component-specific CSS

const ProjectFiles = ({ projectId }) => {
  const { currentUser } = useAuth();
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Check user role for permissions
  const isAdmin = currentUser && currentUser.role === 'admin';
  const isManager = currentUser && currentUser.role === 'manager';

  useEffect(() => {
    fetchFiles();
  }, [projectId]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const projectFiles = await fileService.getProjectFiles(projectId);
      setFiles(projectFiles);
    } catch (err) {
      setError('Failed to load files.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    setLoading(true);
    setError('');
    try {
      // Create an array of promises for each file upload
      const uploadPromises = selectedFiles.map(file => 
        fileService.uploadProjectFile(projectId, file)
      );
      
      // Wait for all uploads to complete
      await Promise.all(uploadPromises);
      setSelectedFiles([]);
      fetchFiles(); // Refresh the file list
    } catch (err) {
      setError('One or more file uploads failed.');
      console.error('Upload error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileId, isBlurred) => {
    let confirmMessage = 'Are you sure you want to delete this file?';
    
    if (isManager && !isBlurred) {
      confirmMessage = 'This will mark the file for deletion. An admin will need to complete the deletion. Continue?';
    } else if (isAdmin && isBlurred) {
      confirmMessage = 'This file was marked for deletion by a manager. Confirm permanent deletion?';
    }
    
    if (!window.confirm(confirmMessage)) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await fileService.deleteProjectFile(fileId);
      
      // If the file was blurred (marked for deletion) by a manager
      if (response.blurred) {
        // Just refresh the list without showing additional messaging
        fetchFiles();
      } else {
        // Regular deletion (by admin)
        fetchFiles();
      }
    } catch (err) {
      setError('Failed to delete file.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handler for restoring files marked for deletion (admin only)
  const handleUnblur = async (fileId) => {
    if (!window.confirm('Restore this file that was marked for deletion?')) return;
    
    setLoading(true);
    setError('');
    try {
      await fileService.unblurFile(fileId);
      fetchFiles(); // Refresh the file list
    } catch (err) {
      setError('Failed to restore file.');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType, fileName) => {
    if (!fileType) return 'bi bi-file-earmark';
    
    // Check file extension
    const extension = fileName ? fileName.split('.').pop().toLowerCase() : '';
    
    // Document types
    if (extension === 'pdf' || fileType.includes('pdf')) return 'bi bi-file-earmark-pdf';
    if (extension === 'doc' || extension === 'docx' || fileType.includes('word') || fileType.includes('document')) return 'bi bi-file-earmark-word';
    
    // Spreadsheets
    if (extension === 'xls' || extension === 'xlsx' || extension === 'csv' || fileType.includes('excel') || fileType.includes('spreadsheet')) 
      return 'bi bi-file-earmark-excel';
      
    // Presentations
    if (extension === 'ppt' || extension === 'pptx' || fileType.includes('presentation')) 
      return 'bi bi-file-earmark-richtext';
    
    // Images
    if (fileType.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension)) 
      return 'bi bi-file-earmark-image';
      
    // Video
    if (fileType.includes('video') || ['mp4', 'webm', 'mov', 'avi', 'wmv'].includes(extension)) 
      return 'bi bi-file-earmark-play';
      
    // Text
    if (fileType.includes('text') || ['txt', 'md', 'rtf'].includes(extension)) 
      return 'bi bi-file-earmark-text';
      
    return 'bi bi-file-earmark';
  };
  
  // Function to handle file preview
  const handleFilePreview = async (fileId, fileName) => {
    try {
      setLoading(true);
      if (fileService.isFileViewable(fileName)) {
        await fileService.previewFile(fileId, fileName);
      } else {
        // If not viewable, download it instead
        await fileService.downloadFile(fileId);
      }
    } catch (err) {
      setError('Failed to preview file. ' + (err.response?.data?.message || err.message));
      console.error('Preview error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-3">
      <h6 className="mb-2"><i className="bi bi-file-earmark me-2"></i>Files</h6>
      {error && <Alert variant="danger" className="py-1 mb-2">{error}</Alert>}
      
      <Row className="mb-2 align-items-end">
        <Col>
          <Form.Control type="file" size="sm" onChange={handleFileChange} multiple />
        </Col>
        <Col xs="auto">
          <Button size="sm" onClick={handleUpload} disabled={!selectedFiles.length || loading}>
            {loading ? <Spinner as="span" animation="border" size="sm" /> : <><i className="bi bi-upload me-1"></i>Upload {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}</>}
          </Button>
        </Col>
      </Row>
      
      {loading && !files.length ? (
        <div className="text-center py-2"><Spinner size="sm" animation="border" /></div>
      ) : files.length > 0 ? (
        <ListGroup variant="flush" className="border rounded">
          {files.map((file) => (
            <ListGroup.Item 
              key={file.id} 
              className={`py-2 d-flex justify-content-between align-items-center ${file.pendingDeletion ? 'file-blurred' : ''}`}
            >
              <div className="d-flex align-items-center text-truncate" style={{ maxWidth: '50%', opacity: file.pendingDeletion ? '0.6' : '1' }}>
                <i className={`${getFileIcon(file.file_type, file.file_name)} me-2`}></i>
                <span 
                  className="text-truncate file-name"
                  title={file.file_name}
                >
                  {file.file_name}
                </span>
                {file.file_type && (
                  <Badge bg="light" text="dark" className="ms-1 d-none d-md-inline">
                    {file.file_type.split('/')[1]}
                  </Badge>
                )}
              </div>
              <div className="d-flex gap-1">
                <ButtonGroup size="sm">
                  <Button 
                    variant="outline-primary"
                    size="sm"
                    className="px-2 py-0 d-flex align-items-center"
                    onClick={() => handleFilePreview(file.id, file.file_name)}
                    title="View file"
                  >
                    <i className="bi bi-eye"></i>
                  </Button>
                  <Button 
                    variant="outline-success"
                    size="sm"
                    className="px-2 py-0 d-flex align-items-center"
                    onClick={async () => {
                      try {
                        setLoading(true);
                        await fileService.downloadFile(file.id);
                      } catch (err) {
                        setError('Failed to download file. ' + (err.response?.data?.message || err.message));
                        console.error('Download error:', err);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    title="Download file"
                  >
                    <i className="bi bi-download"></i>
                  </Button>
                  {/* Show delete button based on role permissions */}
                  {(isAdmin || isManager) ? (
                    <Button 
                      variant={file.pendingDeletion ? "outline-secondary" : "outline-danger"}
                      size="sm" 
                      className="px-2 py-0 d-flex align-items-center"
                      onClick={() => handleDelete(file.id, file.pendingDeletion)}
                      title={file.pendingDeletion ? "Permanently delete file" : "Delete file"}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  ) : null}
                  
                  {/* Show restore button for admins on blurred files */}
                  {(isAdmin && file.pendingDeletion) ? (
                    <Button 
                      variant="outline-success" 
                      size="sm" 
                      className="px-2 py-0 d-flex align-items-center ml-1"
                      onClick={() => handleUnblur(file.id)}
                      title="Restore file"
                    >
                      <i className="bi bi-arrow-counterclockwise"></i>
                    </Button>
                  ) : null}
                </ButtonGroup>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      ) : (
        <div className="text-center text-muted small py-2">No files uploaded</div>
      )}
    </div>
  );
};

export default ProjectFiles;
